import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import {
  Loader2, ShieldOff, Save, Mail, Eye, RotateCcw, FileText, Upload, Trash2, ArrowLeft,
} from 'lucide-react';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import { useToast } from '@/components/ui/use-toast';
import EmailColorControls from '@/components/admin/EmailColorControls';

const DEFAULTS = {
  accent_color: '#dd1f26',
  company_name: 'Motion Reality Sim Racing Ltd',
  company_address:
    'Unit 9, Croft Business Park\nCarrock Road\nBromborough\nWirral\nCH62 3RA\nTel: 0151 528 3001\nEmail: accounts@motionreality.co.uk\nVAT Reg No: 491158870',
  invoice_title: 'INVOICE',
  invoice_number_prefix: 'INV-',
  invoice_number_padding: 5,
  next_invoice_number: 1,
  next_credit_note_number: 1,
  logo_url: 'https://cdn.ecommercedns.uk/files/3/265983/3/51629143/logo.png',
  invoice_font_url: '',
  invoice_font_bold_url: '',
  payment_note: 'Payment has been received in full via {method}. Thank you for your order.',
  payment_status: 'Paid in Full via Card / Square Online',
  bank_sort_code: '30-54-66',
  bank_account_no: '78152960',
  bank_iban: 'GB55LOYD30546678152960',
  bank_bic: 'LOYDGB21F95',
  payment_terms: 'Due on Receipt',
  footer_line_1:
    'Motion Reality Sim Racing Ltd · Unit 9, Croft Business Park, Bromborough, Wirral CH62 3RA · VAT Reg No: 491 158 870',
  footer_line_2:
    'Registered Office: 3rd Floor, 86-90 Paul Street, London, England, EC2A 4NE  ·  Company Reg No: 16171282',
  email_bg_color: '#f4f4f5',
  email_card_bg: '#ffffff',
  email_summary_bg: '#fafafa',
  email_footer_bg: '#f4f4f5',
  email_heading_color: '#111114',
  email_body_color: '#4b5563',
  email_muted_color: '#dd1f26',
  email_footer_color: '#9ca3af',
  email_border_color: '#e5e7eb',
  email_subject: 'Your Motion Reality invoice — {invoice_number}',
  email_heading: 'Your order is confirmed',
  email_greeting: 'Hi {first_name},',
  email_intro:
    'Thank you for your order with Motion Reality Sim Racing Ltd. Your VAT invoice is attached below for your records.',
  email_closing:
    'If you have any questions about your order, our team is here to help — call us on 0151 528 3001 or use the support button below.',
  email_signoff: '— The Motion Reality Team',
  credit_note_title: 'CREDIT NOTE',
  cn_email_subject: 'Your Motion Reality credit note — {credit_note_number}',
  cn_email_heading: 'Your refund is confirmed',
  cn_email_greeting: 'Hi {first_name},',
  cn_email_intro:
    'We have processed a refund for your order with Motion Reality Sim Racing Ltd. Your credit note is attached below for your records.',
  cn_email_closing:
    'Please allow 3–5 business days for the funds to appear in your account. If you have any questions, call us on 0151 528 3001 or use the support button below.',
  cn_email_signoff: '— The Motion Reality Team',
};

const SAMPLE_ORDER = {
  id: 'PREVIEW-0001',
  customer_name: 'Alex Sample',
  customer_email: 'preview@example.com',
  customer_phone: '0151 528 3001',
  product_name: 'Simucube 2 Pro Direct Drive Wheelbase',
  quantity: 1,
  amount: 1299.0,
  created_date: new Date().toISOString(),
  invoice_number: 'INV-PREVIEW',
  square_payment_id: 'pi_preview_1234',
  source: 'Square',
  status: 'paid',
  delivery_address_line1: '12 Pit Lane',
  delivery_address_line2: '',
  delivery_city: 'Liverpool',
  delivery_postcode: 'L1 8JQ',
};

export default function AdminInvoiceDesign() {
  const { user, isLoadingAuth } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState(DEFAULTS);
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('invoice');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailHtml, setEmailHtml] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [cnPreviewUrl, setCnPreviewUrl] = useState('');
  const [cnPreviewLoading, setCnPreviewLoading] = useState(false);
  const [cnEmailHtml, setCnEmailHtml] = useState('');
  const [cnEmailLoading, setCnEmailLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const previewWrapRef = useRef(null);
  const [previewHeight, setPreviewHeight] = useState(640);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const update = () => setPreviewHeight(Math.round(el.clientWidth * 1.4143) + 46);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const rows = await base44.entities.InvoiceSettings.filter({ setting_key: 'global' }, '-updated_date', 1);
        if (rows && rows[0]) {
          setRecordId(rows[0].id);
          setSettings({ ...DEFAULTS, ...rows[0] });
        }
      } catch (_e) { /* fall back to defaults */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => { if (user?.email) setTestEmail(user.email); }, [user]);

  // Debounced live PDF preview — invoice tab.
  useEffect(() => {
    if (tab !== 'invoice') return;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await base44.functions.invoke('previewInvoice', { settings, order: SAMPLE_ORDER });
        if (res?.data?.dataUrl) setPreviewUrl(res.data.dataUrl);
      } catch (_e) { /* keep last preview */ }
      setPreviewLoading(false);
    }, 600);
    return () => clearTimeout(t);
  }, [settings, tab]);

  // Debounced live email preview — email tab.
  useEffect(() => {
    if (tab !== 'email') return;
    setEmailLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await base44.functions.invoke('previewOrderEmail', { settings, order: SAMPLE_ORDER });
        if (res?.data?.html) setEmailHtml(res.data.html);
      } catch (_e) { /* keep last preview */ }
      setEmailLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [settings, tab]);

  // Debounced live credit note PDF preview — credit note tab (PDF mode).
  useEffect(() => {
    if (tab !== 'credit_note') return;
    setCnPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await base44.functions.invoke('previewCreditNote', { settings, order: SAMPLE_ORDER });
        if (res?.data?.dataUrl) setCnPreviewUrl(res.data.dataUrl);
      } catch (_e) { /* keep last preview */ }
      setCnPreviewLoading(false);
    }, 600);
    return () => clearTimeout(t);
  }, [settings, tab]);

  // Debounced live credit note email preview — credit note email tab.
  useEffect(() => {
    if (tab !== 'credit_note_email') return;
    setCnEmailLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await base44.functions.invoke('previewCreditNoteEmail', { settings, order: SAMPLE_ORDER });
        if (res?.data?.html) setCnEmailHtml(res.data.html);
      } catch (_e) { /* keep last preview */ }
      setCnEmailLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [settings, tab]);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast({ title: 'Logo must be a PNG or JPEG image', variant: 'destructive' });
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await base44.integrations.Core.UploadFile({ file });
      if (res?.file_url) {
        set('logo_url', res.file_url);
        toast({ title: 'Logo uploaded' });
      } else {
        toast({ title: 'Upload failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    }
    setUploadingLogo(false);
  };

  const uploadFont = async (file, key) => {
    if (!file) return;
    if (!/\.(ttf|otf)$/i.test(file.name)) {
      toast({ title: 'Font must be a .ttf or .otf file', variant: 'destructive' });
      return;
    }
    try {
      const res = await base44.integrations.Core.UploadFile({ file });
      if (res?.file_url) {
        set(key, res.file_url);
        toast({ title: 'Font uploaded' });
      } else {
        toast({ title: 'Upload failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (recordId) {
        await base44.entities.InvoiceSettings.update(recordId, { ...settings, setting_key: 'global' });
      } else {
        const created = await base44.entities.InvoiceSettings.create({ ...settings, setting_key: 'global' });
        if (created?.id) setRecordId(created.id);
      }
      toast({ title: 'Design saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const [resetOpen, setResetOpen] = useState(false);
  const resetDefaults = () => {
    setSettings(DEFAULTS);
    setResetOpen(false);
  };

  const sendTest = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast({ title: 'Enter a valid email to send the test to', variant: 'destructive' });
      return;
    }
    setSendingTest(true);
    try {
      const order = { ...SAMPLE_ORDER, customer_email: testEmail };
      const res = await base44.functions.invoke('sendOrderConfirmation', { data: order, settings });
      if (res?.data?.success) {
        toast({ title: 'Test email sent', description: `Order confirmation + invoice sent to ${testEmail}` });
      } else {
        toast({ title: 'Send failed', description: res?.data?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Send failed', description: e.message, variant: 'destructive' });
    }
    setSendingTest(false);
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">MARKETING &amp; COMMS</p>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">Invoice &amp; Email Design</h1>
          <p className="text-white/30 text-sm mt-1 max-w-xl">
            Edit the look of the VAT invoice PDF, order-confirmation email and credit notes. The live preview updates as you type — hit <span className="text-white/50">Save</span> to apply it to all future orders and refunds.
          </p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-8 border-b border-white/10">
              {[
                { key: 'invoice', label: 'Invoice PDF' },
                { key: 'email', label: 'Order Email' },
                { key: 'credit_note', label: 'Credit Note PDF' },
                { key: 'credit_note_email', label: 'Credit Note Email' },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-5 py-3 text-xs font-bold tracking-widest transition-colors -mb-px border-b-2 ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white/70'}`}
                >
                  {t.label.toUpperCase()}
                </button>
              ))}
            </div>

            {tab === 'invoice' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <InvoiceForm settings={settings} set={set} uploadingLogo={uploadingLogo} uploadLogo={uploadLogo} uploadFont={uploadFont} />
                <div className="lg:sticky lg:top-24 self-start">
                  <div className="border border-white/10 bg-[#111115] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-primary" />
                      <p className="text-white/50 text-[10px] font-bold tracking-widest">LIVE PREVIEW</p>
                      {previewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/30 ml-auto" />}
                    </div>
                    <div ref={previewWrapRef} className="w-full">
                      {previewUrl ? (
                        <iframe
                          src={previewUrl + '#toolbar=1&navpanes=0&view=Fit'}
                          title="Invoice preview"
                          className="w-full bg-white border border-white/10"
                          style={{ height: previewHeight }}
                        />
                      ) : (
                        <div className="flex items-center justify-center text-white/30 text-xs" style={{ height: previewHeight }}>
                          Generating preview…
                        </div>
                      )}
                    </div>
                    <p className="text-white/20 text-[11px] mt-2">Preview uses a sample order. Saved design applies to all new purchase emails.</p>
                  </div>
                </div>
              </div>
            )}
            {tab === 'email' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <EmailForm settings={settings} set={set} />
                <div className="lg:sticky lg:top-24 self-start">
                  <div className="border border-white/10 bg-[#111115] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-primary" />
                      <p className="text-white/50 text-[10px] font-bold tracking-widest">EMAIL PREVIEW</p>
                      {emailLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/30 ml-auto" />}
                    </div>
                    <div className="w-full">
                      {emailHtml ? (
                        <iframe
                          srcDoc={emailHtml}
                          title="Email preview"
                          className="w-full bg-white border border-white/10"
                          style={{ height: previewHeight }}
                          sandbox=""
                        />
                      ) : (
                        <div className="flex items-center justify-center text-white/30 text-xs" style={{ height: previewHeight }}>
                          Generating preview…
                        </div>
                      )}
                    </div>
                    <p className="text-white/20 text-[11px] mt-2">Preview uses a sample order. The accent colour, logo and copy come from your saved design.</p>
                  </div>
                </div>
              </div>
            )}

            {tab === 'credit_note' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <CreditNotePdfForm settings={settings} set={set} />
                <div className="lg:sticky lg:top-24 self-start">
                  <div className="border border-white/10 bg-[#111115] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-primary" />
                      <p className="text-white/50 text-[10px] font-bold tracking-widest">CREDIT NOTE PREVIEW</p>
                      {cnPreviewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/30 ml-auto" />}
                    </div>
                    <div ref={previewWrapRef} className="w-full">
                      {cnPreviewUrl ? (
                        <iframe
                          src={cnPreviewUrl + '#toolbar=1&navpanes=0&view=Fit'}
                          title="Credit note preview"
                          className="w-full bg-white border border-white/10"
                          style={{ height: previewHeight }}
                        />
                      ) : (
                        <div className="flex items-center justify-center text-white/30 text-xs" style={{ height: previewHeight }}>
                          Generating preview…
                        </div>
                      )}
                    </div>
                    <p className="text-white/20 text-[11px] mt-2">Credit notes are generated automatically when a refund is issued. They reuse the invoice layout, fonts, logo and colours.</p>
                  </div>
                </div>
              </div>
            )}

            {tab === 'credit_note_email' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <CreditNoteEmailForm settings={settings} set={set} />
                <div className="lg:sticky lg:top-24 self-start">
                  <div className="border border-white/10 bg-[#111115] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-primary" />
                      <p className="text-white/50 text-[10px] font-bold tracking-widest">EMAIL PREVIEW</p>
                      {cnEmailLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/30 ml-auto" />}
                    </div>
                    <div className="w-full">
                      {cnEmailHtml ? (
                        <iframe
                          srcDoc={cnEmailHtml}
                          title="Credit note email preview"
                          className="w-full bg-white border border-white/10"
                          style={{ height: previewHeight }}
                          sandbox=""
                        />
                      ) : (
                        <div className="flex items-center justify-center text-white/30 text-xs" style={{ height: previewHeight }}>
                          Generating preview…
                        </div>
                      )}
                    </div>
                    <p className="text-white/20 text-[11px] mt-2">The credit note email reuses the same accent colour, logo and email colours as the order email (Order Email tab).</p>
                  </div>
                </div>
              </div>
            )}

            {/* Shared actions */}
            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE DESIGN
              </button>
              <button
                onClick={() => setResetOpen(true)}
                className="inline-flex items-center gap-2 border border-white/10 hover:border-white/25 text-white/50 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> RESET TO DEFAULTS
              </button>
            </div>

            {/* Test email */}
            <div className="border border-primary/20 bg-primary/[0.03] p-6 mt-6">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-2">SEND TEST EMAIL</p>
              <p className="text-white/40 text-xs mb-3 leading-relaxed">
                Sends a real order-confirmation email (with the invoice PDF + HTML summary) to the address below, using your current edits. Save to keep them for live orders.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="seo-input flex-1"
                />
                <button
                  onClick={sendTest}
                  disabled={sendingTest}
                  className="inline-flex items-center justify-center gap-2 border border-primary/40 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-xs tracking-widest px-5 py-3 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} SEND TEST
                </button>
              </div>
            </div>
          </>
        )}

        <ConfirmDialog
          open={resetOpen}
          title="Reset to defaults"
          description="Reset all fields to the default Motion Reality design? Any unsaved changes will be lost."
          confirmLabel="Reset"
          onConfirm={resetDefaults}
          onClose={() => setResetOpen(false)}
        />
      </section>
    </div>
  );
}

function InvoiceForm({ settings, set, uploadingLogo, uploadLogo, uploadFont }) {
  return (
    <div className="space-y-5">
      <div className="border border-white/10 bg-[#111115] p-6 space-y-5">
        <div className="border border-primary/20 bg-primary/[0.03] p-4">
          <p className="text-primary text-[10px] font-bold tracking-widest mb-2">SEQUENTIAL INVOICE NUMBERING</p>
          <p className="text-white/40 text-xs leading-relaxed mb-3">
            Set the next number to continue after your last Sage invoice. Each new invoice increments this by 1 automatically, keeping numbers sequential and consistently formatted.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="PREFIX">
              <input value={settings.invoice_number_prefix} onChange={(e) => set('invoice_number_prefix', e.target.value)} className="seo-input" placeholder="INV-" />
            </Field>
            <Field label="PADDING (digits)">
              <input type="number" min="1" max="10" value={settings.invoice_number_padding} onChange={(e) => set('invoice_number_padding', Number(e.target.value) || 5)} className="seo-input" />
            </Field>
            <Field label="NEXT INVOICE NO.">
              <input type="number" min="1" value={settings.next_invoice_number} onChange={(e) => set('next_invoice_number', Number(e.target.value) || 1)} className="seo-input" />
            </Field>
          </div>
          <p className="text-white/30 text-[11px] mt-3">
            Next invoice will be: <span className="text-white/70 font-mono font-bold">{settings.invoice_number_prefix}{String(settings.next_invoice_number || 1).padStart(settings.invoice_number_padding || 5, '0')}</span>
          </p>
          <div className="mt-3 pt-3 border-t border-white/10">
            <Field label="NEXT CREDIT NOTE NO.">
              <input type="number" min="1" value={settings.next_credit_note_number} onChange={(e) => set('next_credit_note_number', Number(e.target.value) || 1)} className="seo-input max-w-xs" />
            </Field>
            <p className="text-white/30 text-[11px] mt-1.5">Credit notes use a fixed <span className="font-mono">CN-</span> prefix with the same padding.</p>
          </div>
        </div>
        <Field label="COMPANY NAME">
          <input value={settings.company_name} onChange={(e) => set('company_name', e.target.value)} className="seo-input" />
        </Field>
        <Field label="COMPANY ADDRESS (one line per row)" textarea>
          <textarea value={settings.company_address} onChange={(e) => set('company_address', e.target.value)} rows={4} className="seo-input resize-y" />
        </Field>
        <Field label="INVOICE TITLE">
          <input value={settings.invoice_title} onChange={(e) => set('invoice_title', e.target.value)} className="seo-input" />
        </Field>
        <Field label="LOGO (top-right — PNG/JPEG, hosted on site)">
          <div className="flex items-center gap-3">
            {settings.logo_url && (
              <img src={settings.logo_url} alt="Logo preview" className="w-12 h-12 object-contain border border-white/10 bg-white/5 p-1" />
            )}
            <label className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-white/70 hover:text-white font-bold text-[11px] tracking-widest px-4 py-2.5 cursor-pointer transition-colors">
              {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {settings.logo_url ? 'REPLACE LOGO' : 'UPLOAD LOGO'}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </label>
            {settings.logo_url && (
              <button type="button" onClick={() => set('logo_url', '')} className="inline-flex items-center gap-1.5 text-white/30 hover:text-red-400 text-[11px] font-bold tracking-widest transition-colors" title="Remove logo">
                <Trash2 className="w-4 h-4" /> REMOVE
              </button>
            )}
          </div>
          {settings.logo_url && <p className="text-white/20 text-[10px] mt-2 break-all">{settings.logo_url}</p>}
        </Field>
        <Field label="CUSTOM FONT — REGULAR (TTF/OTF)">
          <FontDrop label="REGULAR" value={settings.invoice_font_url} onUpload={(f) => uploadFont(f, 'invoice_font_url')} onRemove={() => set('invoice_font_url', '')} />
        </Field>
        <Field label="CUSTOM FONT — BOLD (optional)">
          <FontDrop label="BOLD" value={settings.invoice_font_bold_url} onUpload={(f) => uploadFont(f, 'invoice_font_bold_url')} onRemove={() => set('invoice_font_bold_url', '')} />
          <p className="text-white/20 text-[10px] mt-2 leading-relaxed">Optional — used for headings & bold text. If omitted, bold reuses your regular font.</p>
        </Field>
        <Field label="BOX OUTLINE COLOUR (BRAND PALETTE)">
          <div className="flex items-end gap-4 flex-wrap">
            {['#dd1f26', '#000000', '#ffffff'].map((c) => (
              <div key={c} className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => set('accent_color', c)}
                  className={`relative w-11 h-11 border-2 transition-all ${String(settings.accent_color || '').toLowerCase() === c ? 'border-primary ring-2 ring-primary/40' : 'border-white/15 hover:border-white/40'}`}
                  style={{ background: c }}
                  title={c}
                >
                  {String(settings.accent_color || '').toLowerCase() === c && (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color: c === '#ffffff' ? '#000' : '#fff' }}>✓</span>
                  )}
                </button>
                <span className="text-white/40 font-mono text-[10px] uppercase">{c}</span>
              </div>
            ))}
            <div className="flex flex-col items-center gap-1.5">
              <label
                className={`relative w-11 h-11 border-2 cursor-pointer transition-all flex items-center justify-center overflow-hidden ${!['#dd1f26', '#000000', '#ffffff'].includes(String(settings.accent_color || '').toLowerCase()) ? 'border-primary ring-2 ring-primary/40' : 'border-white/15 hover:border-white/40'}`}
                style={{ background: settings.accent_color || '#dd1f26' }}
                title="Custom colour"
              >
                {!['#dd1f26', '#000000', '#ffffff'].includes(String(settings.accent_color || '').toLowerCase()) && (
                  <span className="text-[10px] font-bold" style={{ color: String(settings.accent_color || '').toLowerCase() === '#ffffff' ? '#000' : '#fff' }}>✓</span>
                )}
                <input type="color" value={settings.accent_color || '#dd1f26'} onChange={(e) => set('accent_color', e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
              </label>
              <span className="text-white/40 font-mono text-[10px] uppercase">{settings.accent_color || '#dd1f26'}</span>
            </div>
          </div>
          <p className="text-white/20 text-[10px] mt-3 leading-relaxed">Used for the box outlines and table borders on the invoice and email. The first three are fixed brand colours; the last is a custom picker.</p>
        </Field>
        <Field label="PAYMENT NOTE" textarea>
          <textarea value={settings.payment_note} onChange={(e) => set('payment_note', e.target.value)} rows={2} className="seo-input resize-y" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">
            Use <span className="font-mono text-white/40">{'{method}'}</span> to insert the live payment method for each order. The method is derived from the order source automatically; leave blank to auto-generate the whole note.
          </p>
        </Field>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">PAYMENT METHOD (AUTO-DERIVED — NOT EDITABLE)</p>
          <p className="text-white/50 text-xs leading-relaxed mb-3">
            The payment method shown on each invoice is derived automatically from the order's source (and can be overridden per order). All possible values:
          </p>
          <div className="flex flex-wrap gap-2">
            {['Card / Square', 'Card / Stripe', 'eBay Managed Payment', 'PayPal', 'Card / ShopWired', 'Bank Transfer', 'Card'].map((m) => (
              <span key={m} className="px-2.5 py-1 border border-white/15 text-white/70 text-[11px] font-mono">{m}</span>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">BANK DETAILS (FIXED — NOT EDITABLE)</p>
          <p className="text-white/50 text-xs leading-relaxed mb-3">Bank details are fixed and cannot be changed here. To update them, contact the website owner.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">SORT CODE</p><p className="text-white/80 font-mono text-sm">30-54-66</p></div>
            <div><p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">ACCOUNT NO</p><p className="text-white/80 font-mono text-sm">78152960</p></div>
            <div><p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">IBAN</p><p className="text-white/80 font-mono text-sm break-all">GB55LOYD30546678152960</p></div>
            <div><p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">BIC / SWIFT</p><p className="text-white/80 font-mono text-sm">LOYDGB21F95</p></div>
          </div>
        </div>
        <Field label="PAYMENT TERMS">
          <input value={settings.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} className="seo-input" />
        </Field>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">FOOTER (FIXED — NOT EDITABLE)</p>
          <p className="text-white/50 text-xs leading-relaxed">The invoice footer is fixed and always shows the bank payment details and the registered office / company registration number.</p>
          <p className="text-white/30 text-[11px] mt-3 font-mono break-all">Bank Transfer: Sort Code: 30-54-66 | Account: 78152960 | IBAN: GB55LOYD30546678152960 | BIC: LOYDGB21F95</p>
          <p className="text-white/30 text-[11px] mt-1 font-mono break-all">Registered Office: 3rd Floor, 86-90 Paul Street, London, England, EC2A 4NE · Company Reg No: 16171282</p>
        </div>
      </div>
    </div>
  );
}

function EmailForm({ settings, set }) {
  return (
    <div className="space-y-5">
      <div className="border border-white/10 bg-[#111115] p-6 space-y-5">
        <p className="text-white/40 text-xs leading-relaxed">
          The order-confirmation email uses the same accent colour and logo as the invoice. Edit the text below to customise the email sent with every purchase. The order summary, totals and invoice download button are generated automatically.
        </p>
        <EmailColorControls settings={settings} set={set} />
        <Field label="EMAIL SUBJECT">
          <input value={settings.email_subject} onChange={(e) => set('email_subject', e.target.value)} className="seo-input" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">Use <span className="font-mono text-white/40">{'{invoice_number}'}</span> to insert the live invoice number.</p>
        </Field>
        <Field label="MAIN HEADING">
          <input value={settings.email_heading} onChange={(e) => set('email_heading', e.target.value)} className="seo-input" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">The large headline shown under the "ORDER CONFIRMED" status (which is fixed and cannot be changed). Use <span className="font-mono text-white/40">{'{first_name}'}</span> or <span className="font-mono text-white/40">{'{invoice_number}'}</span> for dynamic values.</p>
        </Field>
        <Field label="GREETING">
          <input value={settings.email_greeting} onChange={(e) => set('email_greeting', e.target.value)} className="seo-input" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">Use <span className="font-mono text-white/40">{'{first_name}'}</span> to insert the customer's first name.</p>
        </Field>
        <Field label="INTRO PARAGRAPH" textarea>
          <textarea value={settings.email_intro} onChange={(e) => set('email_intro', e.target.value)} rows={3} className="seo-input resize-y" />
        </Field>
        <Field label="CLOSING PARAGRAPH" textarea>
          <textarea value={settings.email_closing} onChange={(e) => set('email_closing', e.target.value)} rows={3} className="seo-input resize-y" />
        </Field>
        <Field label="SIGN-OFF">
          <input value={settings.email_signoff} onChange={(e) => set('email_signoff', e.target.value)} className="seo-input" />
        </Field>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">DYNAMIC TOKENS</p>
          <p className="text-white/50 text-xs leading-relaxed">
            Insert these placeholders into any field and they'll be replaced per order:
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="px-2.5 py-1 border border-white/15 text-white/70 text-[11px] font-mono">{'{first_name}'}</span>
            <span className="px-2.5 py-1 border border-white/15 text-white/70 text-[11px] font-mono">{'{invoice_number}'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreditNotePdfForm({ settings, set }) {
  return (
    <div className="space-y-5">
      <div className="border border-white/10 bg-[#111115] p-6 space-y-5">
        <p className="text-white/40 text-xs leading-relaxed">
          Credit notes reuse the invoice layout, logo, fonts and accent colour, so the Invoice PDF tab controls the visual design. Here you only edit the credit note title. Credit notes are generated automatically whenever a refund is issued.
        </p>
        <Field label="CREDIT NOTE TITLE">
          <input value={settings.credit_note_title} onChange={(e) => set('credit_note_title', e.target.value)} className="seo-input" />
        </Field>
        <p className="text-white/20 text-[10px] leading-relaxed">The email sent with the credit note is designed on the <span className="text-white/40">Credit Note Email</span> tab.</p>
      </div>
    </div>
  );
}

function CreditNoteEmailForm({ settings, set }) {
  return (
    <div className="space-y-5">
      <div className="border border-white/10 bg-[#111115] p-6 space-y-5">
        <p className="text-white/40 text-xs leading-relaxed">
          The credit note email reuses the same accent colour, logo and email colours as the order confirmation email (Order Email tab). Edit the credit note email copy below. The credit note summary, totals and download button are generated automatically.
        </p>
        <Field label="EMAIL SUBJECT">
          <input value={settings.cn_email_subject} onChange={(e) => set('cn_email_subject', e.target.value)} className="seo-input" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">Use <span className="font-mono text-white/40">{'{credit_note_number}'}</span> to insert the live credit note number.</p>
        </Field>
        <Field label="MAIN HEADING">
          <input value={settings.cn_email_heading} onChange={(e) => set('cn_email_heading', e.target.value)} className="seo-input" />
          <p className="text-white/20 text-[10px] mt-1.5 leading-relaxed">Shown under the fixed "CREDIT NOTE ISSUED" status. Use <span className="font-mono text-white/40">{'{first_name}'}</span> or <span className="font-mono text-white/40">{'{credit_note_number}'}</span> for dynamic values.</p>
        </Field>
        <Field label="GREETING">
          <input value={settings.cn_email_greeting} onChange={(e) => set('cn_email_greeting', e.target.value)} className="seo-input" />
        </Field>
        <Field label="INTRO PARAGRAPH" textarea>
          <textarea value={settings.cn_email_intro} onChange={(e) => set('cn_email_intro', e.target.value)} rows={3} className="seo-input resize-y" />
        </Field>
        <Field label="CLOSING PARAGRAPH" textarea>
          <textarea value={settings.cn_email_closing} onChange={(e) => set('cn_email_closing', e.target.value)} rows={3} className="seo-input resize-y" />
        </Field>
        <Field label="SIGN-OFF">
          <input value={settings.cn_email_signoff} onChange={(e) => set('cn_email_signoff', e.target.value)} className="seo-input" />
        </Field>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">DYNAMIC TOKENS</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="px-2.5 py-1 border border-white/15 text-white/70 text-[11px] font-mono">{'{first_name}'}</span>
            <span className="px-2.5 py-1 border border-white/15 text-white/70 text-[11px] font-mono">{'{credit_note_number}'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FontDrop({ label, value, onUpload, onRemove }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };
  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`flex items-center gap-3 border border-dashed ${dragging ? 'border-primary bg-primary/5' : 'border-white/15 hover:border-white/30'} px-4 py-3 cursor-pointer transition-colors`}
      >
        <Upload className="w-4 h-4 text-white/50" />
        <span className="text-white/70 font-bold text-[11px] tracking-widest">
          {value ? `REPLACE ${label}` : `UPLOAD ${label}`}
        </span>
        <span className="text-white/30 text-[10px] ml-auto tracking-widest">DRAG &amp; DROP OR CLICK</span>
        <input ref={inputRef} type="file" accept=".ttf,.otf" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {value && (
        <div className="flex items-center gap-2 mt-2">
          <p className="text-white/20 text-[10px] break-all flex-1">{value}</p>
          <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="inline-flex items-center gap-1.5 text-white/30 hover:text-red-400 text-[11px] font-bold tracking-widest transition-colors">
            <Trash2 className="w-4 h-4" /> REMOVE
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}